import { merge, isEqual, forOwn } from 'lodash'
import Immutable from 'immutable'
import invariant from 'invariant'

// Model
import Collection from './collection'
import { HasMany, HasOne, BelongsTo } from './associations'

const setProps = (prototype, names) => {
  try {
    names.forEach((name) => setProp(prototype, name))
  } catch {
    // Handle error
  }
}

const setProp = (prototype, name) => {
  Object.defineProperty(prototype, name, {
    get () {
      return this.get(name)
    },
    set (value) {
      invariant(this.__ownerID, 'Cannot set on an immutable record')
      this.set(name, value)
    }
  })
}

export default class Model extends Immutable.Record({}) {
  constructor (values) {
    super()

    values ||= {}

    const defaultValues = merge({}, this.constructor.prototype._defaultValues)

    // Add keys of value as default values, but as undefined
    // This fixes @hashCode() always being the same
    forOwn(values, (_value, key) => {
      defaultValues[key] = undefined
    })

    const keys = Object.keys(defaultValues)

    setProps(this.constructor.prototype, keys)

    this.constructor.prototype.size = keys.length
    this.constructor.prototype._name = name
    this.constructor.prototype._keys = keys
    this.constructor.prototype._defaultValues = defaultValues

    // Make any key in the values accessible as a property
    setProps(this, Object.keys(values))

    this._map = Immutable.Map(values)
  }

  static defaultValues (defaults = {}) {
    this.prototype._defaultValues ||= {}

    forOwn(defaults, (value, key) => {
      this.prototype._defaultValues[key] = value
    })

    return this.prototype._defaultValues
  }

  static collection () {
    return this._collection || new Collection(this)
  }

  static hasMany (name, options = {}) {
    return new HasMany(this, name, options)
  }

  static hasOne (name, options = {}) {
    return new HasOne(this, name, options)
  }

  static belongsTo (name, options = {}) {
    return new BelongsTo(this, name, options)
  }

  has () {
    return true
  }

  toJSON () {
    return this._map.toJS()
  }

  toJS () {
    return this._map.toJS()
  }

  hydrate (data) {
    // Reject outdated data. This prevents the record from being updated
    // with old data when there is a race condition between requests.
    if (new Date(data.updated_at) < new Date(this.updated_at)) return this

    // Update each value that is different from the oldValue.
    //
    // We do this attribute-by-attribute instead of with one global isEqual(),
    // so that we can preserve object-identity of attributes which are objects,
    // like `order.status_counts` or `line.price_rule_values`.
    //
    const result = this.withMutations((object) => {
      forOwn(data, (newValue, attr) => {
        const oldValue = this.get(attr)
        if (!isEqual(newValue, oldValue)) {
          object.set(attr, newValue)
        }
      })
    })

    setProps(result, Object.keys(result.toJS()))

    return result
  }
}
